import React, { useEffect, useMemo, useState } from 'react';

import {
    Box,
    LinearProgress,
    Paper
} from '@mui/material';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';

import { Alerta } from '../../components/alerta';
import CrmMetricCard from '../../components/crmMetricCard';
import LineChart from '../../components/lineChart';
import MonthRangePicker from '../../components/monthRangePicker';
import MultiSelectFilter from '../../components/multiSelectFilter';
import {
    formatDecimal,
    formatInteger
} from '../../utils/numberUtils';
import {
    ALL_EVENTS_OPTION,
    buildCupons,
    buildCuponsSummary,
    buildDailyCupons,
    buildEventOptions,
    buildSignatureMonthBounds,
    fetchCupons,
    filterCupons
} from './cuponsData';
import '../css/crmDashboard.css';
import '../css/crmCupons.css';

const CUPONS_CHART_MAX_X_TICKS = 12;

const cuponsMetricTone = [
    { accent: 'var(--setta-amber)', helper: 'var(--app-muted)', icon: <ConfirmationNumberRoundedIcon sx={{ fontSize: 18 }} /> },
    { accent: '#7b8eec', helper: '#7b8eec', icon: <LocalOfferRoundedIcon sx={{ fontSize: 18 }} /> },
    { accent: '#2ab47d', helper: '#2ab47d', icon: <TrendingUpRoundedIcon sx={{ fontSize: 18 }} /> },
    { accent: 'var(--app-text)', helper: 'var(--app-muted)', icon: <EventAvailableRoundedIcon sx={{ fontSize: 18 }} /> }
];

// Os rótulos do eixo são curtos porque a série cobre todo o histórico de cupons.
const formatDayMonth = (day) => `${day.slice(8, 10)}/${day.slice(5, 7)}`;

const CuponsHero = () => (
    <div className='crm-dashboard-hero'>
        <h1 className='crm-dashboard-title'>Cupons</h1>
        <p className='crm-dashboard-body crm-dashboard-hero-copy'>
            Uso de cupons por data, filtrado por evento e mês de assinatura.
        </p>
    </div>
);

const CuponsFilters = ({
    eventFilterOptions,
    onEventsChange,
    onSignatureRangeApply,
    selectedEvents,
    signatureBounds,
    signatureRange
}) => (
    <Paper className='crm-dashboard-filter-shell crm-cupons-filter-shell'>
        <div className='crm-dashboard-filter-row crm-cupons-filter-row'>
            <MultiSelectFilter
                className='crm-dashboard-filter-control'
                labelId='cupons-evento-filter-label'
                label='Evento'
                value={selectedEvents}
                options={eventFilterOptions}
                allOptionValue={ALL_EVENTS_OPTION}
                onChange={onEventsChange}
            />

            <div className='crm-dashboard-range-field'>
                <span className='crm-dashboard-range-label'>Assinatura do cliente</span>
                <MonthRangePicker
                    startDate={signatureRange.startDate}
                    endDate={signatureRange.endDate}
                    minDate={signatureBounds.startDate}
                    maxDate={signatureBounds.endDate}
                    onApply={(startDate, endDate) => onSignatureRangeApply({ startDate, endDate })}
                />
            </div>
        </div>
    </Paper>
);

const CuponsSummary = ({ summary }) => {
    const metrics = [
        {
            label: 'Cupons',
            value: formatInteger(summary.total),
            helperText: `${formatInteger(summary.daysWithCupons)} dias com cupom`
        },
        {
            label: 'Eventos',
            value: formatInteger(summary.events),
            helperText: 'eventos no recorte'
        },
        {
            label: 'Pico diário',
            value: formatInteger(summary.peak.cupons),
            helperText: summary.peak.label || 'sem cupons no recorte'
        },
        {
            label: 'Média diária',
            value: formatDecimal(summary.dailyAverage),
            helperText: 'por dia com cupom'
        }
    ];

    return (
        <div className='crm-dashboard-summary-grid crm-cupons-summary-grid'>
            {metrics.map((item, index) => <CrmMetricCard key={item.label} {...item} {...cuponsMetricTone[index]} />)}
        </div>
    );
};

const CrmCupons = () => {
    const [cupons, setCupons] = useState(null);
    const [selectedEvents, setSelectedEvents] = useState([ALL_EVENTS_OPTION]);
    const [signatureRange, setSignatureRange] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [statusAlerta, setStatusAlerta] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);

    useEffect(() => {
        let ignore = false;

        const loadCupons = async () => {
            setIsLoading(true);

            try {
                const rows = await fetchCupons();
                if (ignore) return;

                const nextCupons = buildCupons(rows);
                setCupons(nextCupons);
                setSignatureRange(buildSignatureMonthBounds(nextCupons));
            } catch (error) {
                if (!ignore) {
                    setMessage(error.message);
                    setStatusAlerta('error');
                    setOpenAlerta(true);
                }
            } finally {
                if (!ignore) setIsLoading(false);
            }
        };

        loadCupons();
        return () => { ignore = true; };
    }, []);

    const eventFilterOptions = useMemo(() => [
        { value: ALL_EVENTS_OPTION, label: 'Todos os Eventos' },
        ...buildEventOptions(cupons ?? [])
    ], [cupons]);
    const signatureBounds = useMemo(() => buildSignatureMonthBounds(cupons ?? []), [cupons]);
    const filteredCupons = useMemo(
        () => cupons && signatureRange
            ? filterCupons({ cupons, selectedEvents, signatureRange })
            : [],
        [cupons, selectedEvents, signatureRange]
    );
    const dailyCupons = useMemo(() => buildDailyCupons(filteredCupons), [filteredCupons]);
    const summary = useMemo(
        () => buildCuponsSummary({ cupons: filteredCupons, dailyCupons }),
        [dailyCupons, filteredCupons]
    );

    return (
        <Box className='mainBox crm-dashboard-page crm-cupons-page'>
            <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />

            <div className='crm-dashboard-frame'>
                <div className='crm-dashboard-content'>
                    <div className='crm-dashboard-topbar'>
                        <CuponsHero />
                        {signatureRange && (
                            <CuponsFilters
                                eventFilterOptions={eventFilterOptions}
                                onEventsChange={setSelectedEvents}
                                onSignatureRangeApply={setSignatureRange}
                                selectedEvents={selectedEvents}
                                signatureBounds={signatureBounds}
                                signatureRange={signatureRange}
                            />
                        )}
                    </div>

                    {isLoading && <LinearProgress className='crm-dashboard-loading' variant='indeterminate' />}

                    {cupons && (
                        <>
                            <CuponsSummary summary={summary} />

                            {dailyCupons.length > 0
                                ? (
                                    <LineChart
                                        ariaLabel='Cupons por data'
                                        eyebrow='Cupons por data'
                                        icon={ShowChartRoundedIcon}
                                        maxXTicks={CUPONS_CHART_MAX_X_TICKS}
                                        pointAriaLabel={(point) => `${point.label}: ${formatInteger(point.cupons)} cupons`}
                                        pointKey={(point) => point.day}
                                        peakText={(point) => `Pico: ${formatInteger(point.cupons)} em ${point.label}`}
                                        rows={dailyCupons}
                                        title='Quantitativo diário'
                                        tooltipValue={(point) => `${formatInteger(point.cupons)} cupons`}
                                        valueKey='cupons'
                                        xTickLabel={(point) => formatDayMonth(point.day)}
                                    />
                                )
                                : <p className='crm-cupons-empty'>Nenhum cupom para os filtros selecionados.</p>}
                        </>
                    )}
                </div>
            </div>
        </Box>
    );
};

export default CrmCupons;
